import { toast } from 'sonner';

import { lamportsToSol } from '@/utils/solana/lamportsToSol';
import { useShare } from '@/hooks/useShare';

interface LotteryClaimSuccessModalProps {
  open: boolean;
  onClose: () => void;
  reward: {
    prizeLabel: string;
    prizeAmountLamports: string;
    seasonNo: number;
  };
}

export function LotteryClaimSuccessModal({
  open,
  onClose,
  reward,
}: LotteryClaimSuccessModalProps) {
  const { shareLottery, getPotentialPoints, isSharing } = useShare();

  if (!open) {
    return null;
  }

  const prizeSOL = lamportsToSol(BigInt(reward.prizeAmountLamports || '0'));

  const handleShare = async () => {
    try {
      const result = await shareLottery(prizeSOL);
      if (result.pointsEarned > 0) {
        toast.success(`+${result.pointsEarned} Points!`, {
          description: 'Share tracked successfully',
        });
      } else {
        toast.info('Share opened', {
          description: result.message,
        });
      }
    } catch (error) {
      toast.error('Failed to track share', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-50">
      <div className="relative w-full h-full flex flex-col">
        {/* Top Gray Bar with Congratulations - Full Width */}
        <div
          className="py-6 px-8 text-center"
          style={{
            backgroundColor: 'rgba(40, 40, 40, 0.95)',
            borderTop: '3px solid #50E4F9',
            borderBottom: '3px solid #50E4F9',
          }}
        >
          <h1
            className="text-5xl md:text-6xl font-bold"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: '#FFFFFF',
            }}
          >
            CONGRATULATIONS
          </h1>
        </div>

        {/* Middle Content Area with Video Background */}
        <div className="relative flex-1 flex items-center justify-center overflow-hidden">
          {/* Background Video */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-contain"
            style={{
              filter: 'brightness(0.5)',
              opacity: 0.8,
              transform: 'scale(1.5)',
            }}
          >
            <source src="/chestgif.mp4" type="video/mp4" />
          </video>

          {/* Content Container */}
          <div className="relative z-10 w-full max-w-6xl px-8">
            {/* Reward Section */}
            <div className="mb-6">
              <h2
                className="text-2xl md:text-3xl text-center mb-2"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 700,
                  color: '#FFFFFF',
                }}
              >
                Lottery Rewards
              </h2>
              <p
                className="text-center mb-4"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                Rewards will be distributed to you soon
              </p>

              {/* Horizontal scrollable container - exactly like chest rewards */}
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-4 justify-center" style={{ minWidth: 'min-content' }}>
                  <div
                    className="p-4 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      border: '2px solid #50E4F9',
                      minWidth: '180px',
                    }}
                  >
                    <div className="w-12 h-12 mb-2 flex items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(80, 228, 249, 0.2)' }}>
                      <img
                        src="/solana-sol-logo.svg"
                        alt="Credits"
                        className="w-8 h-8"
                      />
                    </div>
                    <div
                      className="text-2xl font-bold"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      {prizeSOL.toFixed(4)} Credits
                    </div>
                    <div
                      className="text-sm mt-1"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      {reward.prizeLabel} - Season #{reward.seasonNo}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Gray Bar with Action Buttons - Full Width */}
        <div
          className="py-6 px-8"
          style={{
            backgroundColor: 'rgba(40, 40, 40, 0.95)',
            borderTop: '3px solid #50E4F9',
          }}
        >
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <button
              onClick={handleShare}
              disabled={isSharing}
              className="w-full sm:w-auto py-4 px-12 font-bold transition-all text-xl transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
              style={{
                fontFamily: 'Outfit, sans-serif',
                borderRadius: '100px',
                background: 'linear-gradient(135deg, #50E4F9, #00CED1)',
                color: '#000000',
                boxShadow: '0 0 20px rgba(80, 228, 249, 0.6)',
              }}
            >
              {getPotentialPoints('lottery') > 0 ? (
                <>
                  SHARE TO GET {getPotentialPoints('lottery')} <img src="/points.svg" alt="Points" className="w-6 h-6" />
                </>
              ) : (
                'SHARE ON X'
              )}
            </button>
          </div>

          {/* Quit Button */}
          <div className="text-center">
            <button
              onClick={onClose}
              className="text-white underline hover:opacity-70 transition-opacity"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: '18px',
              }}
            >
              QUIT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}